/**
 * Org picker hooks — the group-admin companion to the agent picker.
 *
 *   usePickerOrgs(groupId)   → React Query result of PickerOrg[]
 *   useSelectedOrg()         → { selected, select, clear } reactive
 *                              over localStorage so every mounted
 *                              consumer re-renders on pick.
 *   useEffectiveOrgId()      → user.org_id OR the picked org id,
 *                              used by components that previously
 *                              assumed every user has a fixed org_id
 *                              on their JWT (true for org admins,
 *                              false for group admins).
 *
 * localStorage keys match the names TopNav.safeGet already reads:
 *   "specta.org_id"
 *   "specta.org_name"
 */
import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { useAuth } from "@features/auth/AuthContext";
import { getPickerOrgs } from "../api/orgs.api";
import type { PickerOrg } from "../api/orgs.schemas";

/* ─── React Query: list all orgs in a group ─────────────────────── */

export const usePickerOrgs = (groupId: string) =>
  useQuery({
    queryKey: ["org-picker", groupId],
    queryFn: () => getPickerOrgs(groupId),
    enabled: groupId !== "",
    // Rare mutation — group admin's org list rarely changes mid-session.
    staleTime: 5 * 60_000,
  });

/* ─── Reactive localStorage: the chosen org ─────────────────────── */

const KEY_ID   = "specta.org_id";
const KEY_NAME = "specta.org_name";

/** Lightweight event bus so a `select()` call in one component triggers
 *  a re-render in every other mounted `useSelectedOrg()`. Browser's
 *  built-in `storage` event only fires cross-tab, not same-tab. */
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

const subscribe = (fn: () => void): (() => void) => {
  listeners.add(fn);
  // Also hook the cross-tab storage event so a pick in one tab syncs here.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY_ID || e.key === KEY_NAME) fn();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("storage", onStorage);
  };
};

const safeGet = (k: string): string | null => {
  try { return localStorage.getItem(k); } catch { return null; }
};

const safeSet = (k: string, v: string | null): void => {
  try {
    if (v == null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch { /* storage full / disabled — fall through silently */ }
};

export interface SelectedOrg { org_id: string; name: string }

/** Snapshot source for useSyncExternalStore. Returns a referentially-stable
 *  cached object when the underlying values haven't changed, so React
 *  doesn't flag "snapshot changed between renders". */
let cached: SelectedOrg | null = null;
let cachedKey = "";
const getSnapshot = (): SelectedOrg | null => {
  const id = safeGet(KEY_ID) ?? "";
  const name = safeGet(KEY_NAME) ?? "";
  const key = `${id}|${name}`;
  if (key === cachedKey) return cached;
  cachedKey = key;
  cached = id ? { org_id: id, name: name || id } : null;
  return cached;
};
// Server-side rendering guard — avoids touching localStorage during SSR.
const getServerSnapshot = (): SelectedOrg | null => null;

export const useSelectedOrg = (): {
  selected: SelectedOrg | null;
  select:   (o: SelectedOrg) => void;
  clear:    () => void;
} => {
  const selected = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    selected,
    select: (o) => {
      safeSet(KEY_ID,   o.org_id);
      safeSet(KEY_NAME, o.name);
      notify();
    },
    clear: () => {
      safeSet(KEY_ID,   null);
      safeSet(KEY_NAME, null);
      notify();
    },
  };
};

/** Returns the org id the current UI should query against:
 *    • Group admins switch between the orgs in their group via the TopNav
 *      OrgSelector, so their PICKED org (localStorage) takes precedence.
 *    • Org admins / platform have no picker — they use the org on their JWT.
 *
 *  Why the picked org must win for group admins: the backend may still put
 *  a default/home `org_id` on a group admin's token (the schema allows a
 *  non-null value). If that token org were preferred, switching the org in
 *  the top bar would be ignored — the agents picker query key
 *  (registryKeys.picker(orgId)) and every other org-scoped key would never
 *  change, so the agents API would fire once and never again and the agent
 *  would never swap. Preferring `selected` here is what makes an org change
 *  actually propagate.
 *
 *  Returns "" when neither is available (e.g. group admin on their
 *  first-ever load, before AutoSelectOrg has run). Downstream queries
 *  that key on this should gate `enabled: effectiveOrgId !== ""` so
 *  they don't fire with an empty filter.
 */
export const useEffectiveOrgId = (): string => {
  const { user } = useAuth();
  const { selected } = useSelectedOrg();
  if (user?.scope_level === "group") {
    return selected?.org_id || user?.org_id || "";
  }
  return user?.org_id || selected?.org_id || "";
};

export type { PickerOrg };

// /**
//  * Org picker hooks — the group-admin companion to the agent picker.
//  *
//  *   usePickerOrgs(groupId)   → React Query result of PickerOrg[]
//  *   useSelectedOrg()         → { selected, select, clear } reactive
//  *                              over localStorage so every mounted
//  *                              consumer re-renders on pick.
//  *   useEffectiveOrgId()      → user.org_id OR the picked org id,
//  *                              used by components that previously
//  *                              assumed every user has a fixed org_id
//  *                              on their JWT (true for org admins,
//  *                              false for group admins).
//  *
//  * localStorage keys match the names TopNav.safeGet already reads:
//  *   "specta.org_id"
//  *   "specta.org_name"
//  */
// import { useQuery } from "@tanstack/react-query";
// import { useSyncExternalStore } from "react";
// import { useAuth } from "@features/auth/AuthContext";
// import { getPickerOrgs } from "../api/orgs.api";
// import type { PickerOrg } from "../api/orgs.schemas";

// /* ─── React Query: list all orgs in a group ─────────────────────── */

// export const usePickerOrgs = (groupId: string) =>
//   useQuery({
//     queryKey: ["org-picker", groupId],
//     queryFn: () => getPickerOrgs(groupId),
//     enabled: groupId !== "",
//     // Rare mutation — group admin's org list rarely changes mid-session.
//     staleTime: 5 * 60_000,
//   });

// /* ─── Reactive localStorage: the chosen org ─────────────────────── */

// const KEY_ID   = "specta.org_id";
// const KEY_NAME = "specta.org_name";

// /** Lightweight event bus so a `select()` call in one component triggers
//  *  a re-render in every other mounted `useSelectedOrg()`. Browser's
//  *  built-in `storage` event only fires cross-tab, not same-tab. */
// const listeners = new Set<() => void>();
// const notify = () => listeners.forEach((fn) => fn());

// const subscribe = (fn: () => void): (() => void) => {
//   listeners.add(fn);
//   // Also hook the cross-tab storage event so a pick in one tab syncs here.
//   const onStorage = (e: StorageEvent) => {
//     if (e.key === KEY_ID || e.key === KEY_NAME) fn();
//   };
//   window.addEventListener("storage", onStorage);
//   return () => {
//     listeners.delete(fn);
//     window.removeEventListener("storage", onStorage);
//   };
// };

// const safeGet = (k: string): string | null => {
//   try { return localStorage.getItem(k); } catch { return null; }
// };

// const safeSet = (k: string, v: string | null): void => {
//   try {
//     if (v == null) localStorage.removeItem(k);
//     else localStorage.setItem(k, v);
//   } catch { /* storage full / disabled — fall through silently */ }
// };

// export interface SelectedOrg { org_id: string; name: string }

// /** Snapshot source for useSyncExternalStore. Returns a referentially-stable
//  *  cached object when the underlying values haven't changed, so React
//  *  doesn't flag "snapshot changed between renders". */
// let cached: SelectedOrg | null = null;
// let cachedKey = "";
// const getSnapshot = (): SelectedOrg | null => {
//   const id = safeGet(KEY_ID) ?? "";
//   const name = safeGet(KEY_NAME) ?? "";
//   const key = `${id}|${name}`;
//   if (key === cachedKey) return cached;
//   cachedKey = key;
//   cached = id ? { org_id: id, name: name || id } : null;
//   return cached;
// };
// // Server-side rendering guard — avoids touching localStorage during SSR.
// const getServerSnapshot = (): SelectedOrg | null => null;

// export const useSelectedOrg = (): {
//   selected: SelectedOrg | null;
//   select:   (o: SelectedOrg) => void;
//   clear:    () => void;
// } => {
//   const selected = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
//   return {
//     selected,
//     select: (o) => {
//       safeSet(KEY_ID,   o.org_id);
//       safeSet(KEY_NAME, o.name);
//       notify();
//     },
//     clear: () => {
//       safeSet(KEY_ID,   null);
//       safeSet(KEY_NAME, null);
//       notify();
//     },
//   };
// };

// /** Returns the org id the current UI should query against:
//  *    • Org admins have user.org_id baked into the JWT — use that.
//  *    • Group admins have no fixed org_id; use whichever one they
//  *      picked via the OrgSelector (lives in localStorage).
//  *
//  *  Returns "" when neither is available (e.g. group admin on their
//  *  first-ever load, before AutoSelectOrg has run). Downstream queries
//  *  that key on this should gate `enabled: effectiveOrgId !== ""` so
//  *  they don't fire with an empty filter.
//  */
// export const useEffectiveOrgId = (): string => {
//   const { user } = useAuth();
//   const { selected } = useSelectedOrg();
//   return user?.org_id || selected?.org_id || "";
// };

// export type { PickerOrg };
